import { useState } from "react";
import PropTypes from "prop-types";
import cn from "classnames";

import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion.js";
import createRipple from "../../util/createRipple.js";
import LdsIcon from "../LdsIcon/index.jsx";

// JSDoc comments to supply typescript compiler with the necessary types
/**
 * @typedef FigureCaptionProps
 * @property {string} [ariaLabel] - The ARIA label value for the HTML figure element. Must be set for accessibility compliance; if not explicitly then a default value is used
 * @property {string} [buttonIcon] - Name of icon to show when the description is collapsed. Can be `CaretCircleUpFill`, `CaretUp`, `CaretUpFill`, `MinusCircle`, `MinusCircleFill`, `Minus`
 * @property {string} [buttonId] - The ID for the button that toggles the description.
 * @property {string} [buttonTextAlign] - Button text alignment. Can be `left`, `center`, or `right`
 * @property {React.ReactNode} children - The media to display in the figure. Passed as a child of the component.
 * @property {string} [className] - Optional custom user-defined CSS class(es) for the root figure container element, will be appended to the list of any other component classes.
 * @property {string} [contentMaxHeight] - The maximum height of the description.
 * @property {number} [contentMaxHeightOverride] - Pixel height of the description
 * @property {string} [hideDescriptionText] - Text to display in the hide description button
 * @property {boolean} [initExpanded] - If true expands the figure caption by default. Only applies when noCollapse is false.
 * @property {boolean} [noCollapse] - If true, removes the button and panel to display the text directly below the figure. The text is by default centered.
 * @property {string} [radiusSize] - Size of the border radius, selected from either `small`, `medium`, or `large`
 * @property {string} [showDescriptionText] - Text to display in the show description button
 * @property {string|React.ReactNode} [textContent] - Text to show in the description. Can be any valid HTML. Accepts a string or HTML.
 */
/**
 * FigureCaption component
 * @param {FigureCaptionProps & React.InputHTMLAttributes<HTMLInputElement>} props
 * @returns {React.JSX.Element} - FigureCaption component
 */
export default function FigureCaption({
  ariaLabel = "Figure with description",
  buttonIcon = "CaretCircleUpFill",
  buttonId,
  buttonTextAlign = "left",
  children,
  className,
  contentMaxHeight,
  contentMaxHeightOverride,
  hideDescriptionText = "Close description",
  initExpanded = false,
  noCollapse = false,
  radiusSize = "medium",
  showDescriptionText = "Open description",
  textContent,
  ...rest
}) {
  const [transcriptOpen, setTranscriptOpen] = useState(initExpanded);
  const prefersReducedMotion = usePrefersReducedMotion();

  const swapIcon = () => {
    // return true if icon should be swapped
    return (
      buttonIcon === "MinusCircle" ||
      buttonIcon === "MinusCircleFill" ||
      buttonIcon === "Minus"
    );
  };

  const swappedIconName = () => {
    // get partner icon name
    if (buttonIcon === "MinusCircle") {
      return "PlusCircle";
    }
    if (buttonIcon === "MinusCircleFill") {
      return "PlusCircleFill";
    }
    return "Plus";
  };

  const getMaxHeight = () => {
    if (contentMaxHeightOverride > 0) {
      return `${contentMaxHeightOverride}px`;
    }
    if (contentMaxHeight === "small") {
      return "232px";
    }
    if (contentMaxHeight === "medium") {
      return "472px";
    }
    if (contentMaxHeight === "large") {
      return "712px";
    }
    return "none";
  };

  const handleClick = (event) => {
    // If the user prefers reduced motion don't create the ripple effect
    if (!prefersReducedMotion) {
      createRipple(event);
    }
    setTranscriptOpen(!transcriptOpen);
  };

  return (
    <figure
      aria-label={ariaLabel}
      className={cn(
        "lds-figure-caption",
        `radius-${radiusSize}`,
        {
          collapsed: !transcriptOpen && !noCollapse,
        },
        className
      )}
      {...rest}
    >
      {children}
      {!noCollapse && (
        <button
          id={buttonId}
          type="button"
          className={cn(
            "lds-button-base",
            "lds-figure-caption-toggle",
            `align-${buttonTextAlign}`
          )}
          onClick={(e) => handleClick(e)}
        >
          <LdsIcon
            className={cn("lds-figure-caption-toggle-icon", {
              "icon-swap": swapIcon(),
            })}
            name={
              swapIcon() && !transcriptOpen ? swappedIconName() : buttonIcon
            }
            decorative
          />
          {transcriptOpen ? hideDescriptionText : showDescriptionText}
        </button>
      )}
      <figcaption>
        <div className="lds-figure-caption-text-wrapper">
          <div className="lds-figure-caption-text">
            <div
              className="lds-figure-caption-text-scroll"
              style={{ maxHeight: getMaxHeight() }}
            >
              {textContent}
            </div>
          </div>
        </div>
      </figcaption>
    </figure>
  );
}

FigureCaption.propTypes = {
  ariaLabel: PropTypes.string,
  buttonIcon: PropTypes.PropTypes.oneOf([
    "CaretCircleUpFill",
    "CaretUp",
    "CaretUpFill",
    "MinusCircle",
    "MinusCircleFill",
    "Minus",
  ]),
  buttonId: PropTypes.string,
  buttonTextAlign: PropTypes.PropTypes.oneOf(["left", "center", "right"]),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  contentMaxHeight: PropTypes.oneOf(["small", "medium", "large"]),
  contentMaxHeightOverride: PropTypes.number,
  hideDescriptionText: PropTypes.string,
  initExpanded: PropTypes.bool,
  noCollapse: PropTypes.bool,
  radiusSize: PropTypes.PropTypes.oneOf(["small", "medium", "large"]),
  showDescriptionText: PropTypes.string,
  textContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
    .isRequired,
};
